import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { eventZstore } from '../mmkv/mmkvEventZStore';
import { MMKVStoreKeyMap } from '../mmkv/mmkvStoreKeyMap';
import { IRazorPayPaymentMethod } from '@ninjacart/nc-nk-core';

export interface PlatformSpecificConfig {
  forceVersion: boolean;
  minVersion: string;
  minVersionCode: number;
  activeVersionCode?: number;
}

export interface Language {
  key: string;
  label: string;
  value: string;
}

export interface tourGuideKey {
  enableFollowTour?: boolean;
  enableProfileTour?: boolean;
  enableHomeTour?: boolean;
}

export interface AppBootTimeStore {
  appStartingTime: number;
  setAppStartingTime: (any) => void;
  setAndroidPlatform: (payload: PlatformSpecificConfig) => void;
  setIOSPlatform: (payload: PlatformSpecificConfig) => void;
  androidPlatform?: PlatformSpecificConfig;
  iosPlatform?: PlatformSpecificConfig;
  truecallerEnabled: boolean;
  setTrueCallerEnabled: (truecallerEnabled: boolean) => void;
  globalSearchEnabled: boolean;
  setGlobalSearchEnabled: (globalSearchEnabled: boolean) => void;
  mandiNewsEnabled: boolean;
  setMandiNewsEnabled: (mandiNewsEnabled: boolean) => void;
  homeHeaderEnabled: boolean;
  setHomeHeaderEnabled: (homeHeaderEnabled: boolean) => void;
  newUserLoginRedirectPath: string;
  setNewUserLoginRedirectPath: (id: string) => void;
  firstTimeUser: boolean;
  setFirstTimeUser: (isFirstTime: boolean) => void;
  playListTitle: string;
  setPlayListTitle: (title: string) => void;
  languages: Language[];
  setLanguages: (languages: Language[]) => void;
  autoVideoPlayer: boolean;
  setAutoVideoPlayer: (autoVideoPlayer: boolean) => void;

  isVideoplayerAdEnable: boolean;
  setIsVideoplayerAdEnable: (isVideoplayerAdEnable: boolean) => void;
  contactUsWhatsappNumber: string;
  setContactUsWhatsappNumber: (languageLabel: string) => void;
  contactIndex: number;
  setContactIndex: (contactIndex: number) => void;
  bannerAdKeywords: string[];
  setBannerAdKeywords: (bannerAdKeywords: string[]) => void;
  shortsAdFrequency: number;
  setShortsAdFrequency: (shortsAdFrequency: number) => void;

  shortsAdId: string;
  setShortsAdId: (shortsAdId: string) => void;
  showHotFixPopup: boolean;
  setShowHotFixPopup: (showPopup: boolean) => void;
  followTourGuideEnabled: boolean;
  setFollowTourGuideEnabled: (followTourGuideEnabled: boolean) => void;
  isActiveTourGuides: tourGuideKey;
  setIsActiveTourGuides: (isActiveTourGuides: tourGuideKey) => void;
  userFirstOpenTime: string;
  setUserFirstOpenTime: (userFirstOpenTime: string) => void;
  languageBannerShow: boolean;
  setLanguageBannerShow: (languageBannerShow: boolean) => void;
  weatherUnitId: string;
  setWeatherUnitId: (weatherUnitId: string) => void;
  autoPlayBannerADEnabled: boolean;
  setAutoPlayBannerADEnabled: (isEnabled: boolean) => void;
  razorPayPaymentMethod: IRazorPayPaymentMethod;
  setRazorPayPaymentMethod: (payload: IRazorPayPaymentMethod) => void;
  setTourGuideEnabled?: (enabled: boolean) => void;
  tourGuideEnabled?: boolean;
  homeCustomTabEnabled: boolean;
  setHomeCustomTabEnabled: (homeCustomTabEnabled: boolean) => void;
  enableGlobalTabMic: boolean;
  setEnableGlobalTabMic: (enableGlobalTabMic: boolean) => void;
  enableGlobalProfileTabMic: boolean;
  setEnableGlobalProfileTabMic: (enableGlobalProfileTabMic: boolean) => void;
  enableLanguageIcon: boolean;
  setEnableLanguageIcon: (enableLanguageIcon: boolean) => void;
  profileTabLanguageFilter: string[];
  setProfileTabLanguageFilter: (profileTabLanguageFilter: []) => void;
  adTimershow: number;
  setAdTimerShow: (adTimerShow: number) => void;
  reactVideoMuteEnable: boolean;
  setReactVideoMuteEnable: (show: boolean) => void;
  micTabRefreshCount: number;
  setMicTabRefreshCount: (count: number) => void;
  enableAudioListenerPlatforms: string[];
  setEnableAudioListenerPlatforms: (platforms: string[]) => void;
  appOpenCount: number;
  setAppOpenCount: (count: number) => void;
  isAutoPlaying: boolean;
  setIsAutoPlaying: (isAutoPlaying: boolean) => void;
  adsEndVideoEnabled: boolean;
  setAdsEndVideoEnabled: (value: boolean) => void;
  initializeAdsShow: boolean;
  setInitializeAdsShow: (value: boolean) => void;
}

export const useEventZStore = create<AppBootTimeStore>()(
  persist(
    (set, get) => ({
      appStartingTime: 0,
      setAppStartingTime: (count: number) => set(() => ({ appStartingTime: count })),
      androidPlatform: undefined,
      iosPlatform: undefined,
      setAndroidPlatform: (payload: PlatformSpecificConfig) =>
        set(() => ({ androidPlatform: payload })),
      setIOSPlatform: (payload: PlatformSpecificConfig) => set(() => ({ iosPlatform: payload })),
      truecallerEnabled: false,
      setTrueCallerEnabled: (enabled: boolean) => set(() => ({ truecallerEnabled: enabled })),
      tourGuideEnabled: false,
      setTourGuideEnabled: (enabled: boolean) => set(() => ({ tourGuideEnabled: enabled })),
      globalSearchEnabled: false,
      setGlobalSearchEnabled: (enabled: boolean) => set(() => ({ globalSearchEnabled: enabled })),
      mandiNewsEnabled: false,
      setMandiNewsEnabled: (enabled: boolean) => set(() => ({ mandiNewsEnabled: enabled })),
      homeHeaderEnabled: false,
      setHomeHeaderEnabled: (enabled: boolean) => set(() => ({ homeHeaderEnabled: enabled })),
      newUserLoginRedirectPath: '',
      setNewUserLoginRedirectPath: (id: string) => set(() => ({ newUserLoginRedirectPath: id })),
      firstTimeUser: true,
      setFirstTimeUser: (isFirstTime: boolean) => set(() => ({ firstTimeUser: isFirstTime })),
      playListTitle: '',
      setPlayListTitle: (title: string) => set(() => ({ playListTitle: title })),
      languages: [],
      setLanguages: (languages: Language[]) => set(() => ({ languages })),
      autoVideoPlayer: true,
      setAutoVideoPlayer: (autoVideoPlayer: boolean) =>
        set(() => ({ autoVideoPlayer: autoVideoPlayer })),
      setContactUsWhatsappNumber: (whatsappNumber: string) =>
        set(() => ({ contactUsWhatsappNumber: whatsappNumber })),
      isVideoplayerAdEnable: false,
      setIsVideoplayerAdEnable: (isVideoplayerAdEnable: boolean) =>
        set(() => ({ isVideoplayerAdEnable: isVideoplayerAdEnable })),
      contactUsWhatsappNumber: '',
      setContactIndex: (contactIndex: number) => set(() => ({ contactIndex: contactIndex })),
      contactIndex: 0,
      bannerAdKeywords: [],
      setBannerAdKeywords: (bannerAdKeywords: string[]) => set(() => ({ bannerAdKeywords })),
      shortsAdFrequency: 5,
      setShortsAdFrequency: (shortsAdFrequency: number) => set(() => ({ shortsAdFrequency })),
      shortsAdId: '',
      setShortsAdId: (shortsAdId: string) => set(() => ({ shortsAdId })),
      showHotFixPopup: false,
      setShowHotFixPopup: (showHotFixPopup: boolean) => set(() => ({ showHotFixPopup })),
      followTourGuideEnabled: false,
      setFollowTourGuideEnabled: (enabled: boolean) =>
        set(() => ({ followTourGuideEnabled: enabled })),
      isActiveTourGuides: {},
      setIsActiveTourGuides: (isActiveTourGuides: tourGuideKey) =>
        set(() => ({ isActiveTourGuides: isActiveTourGuides })),
      userFirstOpenTime: '',
      setUserFirstOpenTime: (userFirstOpenTime: string) => set(() => ({ userFirstOpenTime })),
      languageBannerShow: false,
      setLanguageBannerShow: (languageBannerShow: boolean) =>
        set(() => ({ languageBannerShow: languageBannerShow })),
      weatherUnitId: '/23198695442/kisan_app/homepage_infeed_top',
      setWeatherUnitId: (weatherUnitId: string) => set(() => ({ weatherUnitId })),
      autoPlayBannerADEnabled: true,
      setAutoPlayBannerADEnabled: (autoPlayBannerADEnabled: boolean) =>
        set(() => ({ autoPlayBannerADEnabled })),
      razorPayPaymentMethod: {
        card: false,
        upi: false,
        netbanking: false,
        wallet: false,
        emi: false,
      },
      setRazorPayPaymentMethod: (payload: IRazorPayPaymentMethod) =>
        set(() => ({ razorPayPaymentMethod: payload })),
      homeCustomTabEnabled: false,
      setHomeCustomTabEnabled: (enabled: boolean) => set(() => ({ homeCustomTabEnabled: enabled })),
      enableGlobalTabMic: false,
      setEnableGlobalTabMic: (enabled: boolean) => set(() => ({ enableGlobalTabMic: enabled })),
      enableGlobalProfileTabMic: false,
      setEnableGlobalProfileTabMic: (enabled: boolean) =>
        set(() => ({ enableGlobalProfileTabMic: enabled })),
      enableLanguageIcon: false,
      setEnableLanguageIcon: (enabled: boolean) => set(() => ({ enableLanguageIcon: enabled })),
      profileTabLanguageFilter: [],
      setProfileTabLanguageFilter: (profileTabLanguageFilter: []) =>
        set(() => ({ profileTabLanguageFilter: profileTabLanguageFilter })),
      adTimershow: 0,
      setAdTimerShow: (adTimershow: number) => set(() => ({ adTimershow })),
      reactVideoMuteEnable: true,
      setReactVideoMuteEnable: (show: boolean) => set(() => ({ reactVideoMuteEnable: show })),
      micTabRefreshCount: 0,
      setMicTabRefreshCount: (count) => set({ micTabRefreshCount: count }),
      enableAudioListenerPlatforms: [],
      setEnableAudioListenerPlatforms: (platforms: string[]) =>
        set(() => ({ enableAudioListenerPlatforms: platforms })),
      appOpenCount: 0,
      setAppOpenCount: (count: number) => set(() => ({ appOpenCount: count })),
      isAutoPlaying: false,
      setIsAutoPlaying: (show: boolean) => set(() => ({ isAutoPlaying: show })),
      adsEndVideoEnabled: false,
      setAdsEndVideoEnabled: (value) => set({ adsEndVideoEnabled: value }),
      initializeAdsShow: true,
      setInitializeAdsShow: (value) => set({ initializeAdsShow: value }),
    }),
    {
      name: MMKVStoreKeyMap.eventStore,
      storage: createJSONStorage(() => eventZstore),
      partialize: (state) => ({
        firstTimeUser: state.firstTimeUser,
        languages: state.languages,
        contactIndex: state.contactIndex,
        languageBannerShow: state.languageBannerShow,
        autoPlayBannerADEnabled: state.autoPlayBannerADEnabled,
        userFirstOpenTime: state.userFirstOpenTime,
        appOpenCount: state.appOpenCount,
      }),
    }
  )
);
